import './CommLoopsList.scss';
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import DataTable from 'src/component/UI/DataTable';

import { fetchCommLoops } from 'src/module/device/action';
import { getCommLoops, getCommLoopsPageInfo } from 'src/module/device/selector';

import { formatNumberByCommas } from 'src/utils/utils';
import DeviceLink from 'src/component/UI/DeviceLink';
import CircleStatusIndicator from './CircleStatusIndicator';
import variables from 'src/style/variable/variables.module.scss';
import Tooltip from '@mui/material/Tooltip';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'src/i18n';
import TimePeriodToggle, { TimePeriod } from './TimePeriodToggle';

export default function CommLoopsList () {
  const { t } = useTranslation();
  const pageInfo = useSelector(getCommLoopsPageInfo);

  const commLoops = useSelector(getCommLoops);

  const [page, setPage] = useState(pageInfo.get('page', 0));
  const [pageSize, setPageSize] = useState(pageInfo.get('pageSize', 10));
  const [filter, setFilter] = useState(null);
  const [sort, setSort] = useState(null);
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('24hours');

  const dispatch: any = useDispatch();

  const fetchPage = (_page, _pageSize, _filter, _sort) => {
    setPage(_page);
    setPageSize(_pageSize);
    setFilter(_filter);
    setSort(_sort);
  };

  const getStaturationStatusIndicator = (iconStatus) => {
    let indicatorColor = variables.greyLight;
    if (iconStatus === 'error') {
      indicatorColor = variables.error;
    }
    // Low saturation condition (warn before success)
    else if (iconStatus === 'warning') {
      indicatorColor = variables.warning;
    }
    // Success condition
    else if (iconStatus === 'success') {
      indicatorColor = variables.success;
    }
    return (
      <CircleStatusIndicator color={indicatorColor} />
    );

  };

  useEffect(() => {
    const paginationDetails = { page, pageSize, filter, sort };
    dispatch(fetchCommLoops(paginationDetails, timePeriod));
  }, [page, pageSize, filter, sort, timePeriod]);

  const dataColumns = [
    {
      id: 'tag',
      Header: t('commLoops.commLoopTag'),
      accessor: (row: any) => row.get('tag'),
      sortable: true,
      Cell: (row: any) => {
        return <DeviceLink
          label={row.cell.row.original.get('tag')}
          controllerTag={row.cell.row.original.get('tag')}
        />;
      }
    },
    {
      id: 'channelSaturation',
      Header: (<Tooltip title={
        <div>
          <table>
            <tbody>
              <tr>
                <td>GREY</td>
                <td>{t('commLoops.statusNotEnoughData')}</td>
              </tr>
              <tr>
                <td>GREEN</td>
                <td>{t('commLoops.statusNormal')}</td>
              </tr>
              <tr>
                <td>YELLOW</td>
                <td>{t('commLoops.statusModerate')}</td>
              </tr>
              <tr>
                <td>RED</td>
                <td>{t('commLoops.statusHeavy')}</td>
              </tr>
            </tbody>
          </table>
        </div>

      }><div style={{ display: 'flex' }}>{t('common.status')}<InfoIcon className="alert-icon" width={16} height={16} style={{ marginLeft: '5px' }} /></div></Tooltip>),
      accessor: (row: any) => row.get('status'),
      sortable: false,
      Cell: (row: any) => {
        return row.cell.value ?
          getStaturationStatusIndicator(row.cell.value.get('iconStatus')) :
          getStaturationStatusIndicator(null);
      }
    },
    {
      id: 'frog_client_id',
      Header: t('commLoops.supervisorClient'),
      accessor: (row: any) => row.get('frog_client_name'),
      sortable: true,
      Cell: (row: any) => { return row.cell.value || 'N/A'; }
    },
    {
      id: 'ip_address',
      Header: t('commLoops.address'),
      accessor: (row: any) => row.get('ip_address'),
      sortable: true,
      Cell: (row: any) => { return row.cell.value || 'N/A'; }
    },
    {
      id: 'port',
      Header: t('commLoops.port'),
      accessor: (row: any) => row.get('port'),
      sortable: true,
      Cell: (row: any) => { return row.cell.value || 'N/A'; }
    },
    {
      id: 'totalSent',
      Header: t('commLoops.totalMessagesSent'),
      accessor: (row: any) => row.getIn(['status', 'totalSent']),
      sortable: true,
      Cell: (row: any) => { return row.cell.value ? formatNumberByCommas(row.cell.value) : 0; }
    },
    {
      id: 'totalDropped',
      Header: t('commLoops.droppedMessages'),
      accessor: (row: any) => row.getIn(['status', 'totalDropped']),
      sortable: true,
      Cell: (row: any) => { return row.cell.value ? formatNumberByCommas(row.cell.value) : 0; }
    },
    {
      id: 'modbusExceptions',
      Header: t('commLoops.modbusErrors'),
      accessor: (row: any) => row.getIn(['status', 'modbusExceptions']),
      sortable: true,
      Cell: (row: any) => { return row.cell.value ? formatNumberByCommas(row.cell.value) : 0; }
    },
    {
      id: 'totalSkipped',
      Header: t('commLoops.totalSkipped'),
      accessor: (row: any) => row.getIn(['status', 'totalSkipped']),
      sortable: true,
      Cell: (row: any) => { return row.cell.value ? formatNumberByCommas(row.cell.value) : 0; }
    },
    {
      id: 'commFailRatio',
      Header: t('commLoops.commFailRatio'),
      accessor: (row: any) => {
        const errors = row.getIn(['status', 'modbusExceptions']);
        const total = row.getIn(['status', 'totalSent']);
        let percentage: any = 0;
        if (total > 0 && errors > 0) {
          percentage = (errors * 100) / total;
          return Number.isInteger(percentage) ? `${parseInt(percentage)}%` : `${percentage?.toFixed(2)}%`;
        }
        return `0%`;
      },
      disableSortBy: true
    },
    {
      id: 'avgMessageTime',
      Header: t('commLoops.avgMessageTime'),
      accessor: (row: any) => row.getIn(['status', 'avgMessageTime']),
      sortable: true,
      Cell: (row: any) => { return row.cell.value ? `${formatNumberByCommas(parseInt(row.cell.value))} ms` : `0 ms`; }
    }
  ];


  return (
    <div className='comm-loops-list' data-testid='comm-loops-list'>
      <h2>
        {t('nav.commLoops')}
      </h2>
      <div className="comm-loops-list-controls">
        <div className="comm-loops-list-controls-inner">
          <TimePeriodToggle
            value={timePeriod}
            onChange={setTimePeriod}
            tooltip={t('commLoops.timePeriodTooltip')}
          />
          <Tooltip title={t('commLoops.statsPeriodTooltip')} className="comm-loops-list-controls-tooltip">
            <InfoIcon className="alert-icon" width={16} height={16} />
          </Tooltip>
        </div>
      </div>
      <DataTable
        filterable
        filterLabel={t('commLoops.searchCommLoops')}
        columns={dataColumns}
        data={commLoops}
        pagination
        numPages={pageInfo.get('totalPages', 0)}
        totalRecords={pageInfo.get('total', 0)}
        loading={pageInfo.get('loading', false)}
        fetchPage={fetchPage}
        columnWidths={['15%', '10%', '10%', '10%', '10%', '10%', '10%', '10%']}
        tableId='commLoopsTable'
      />
    </div>
  );
}

CommLoopsList.propTypes = {
};

CommLoopsList.defaultProps = {
};
